import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { type JSX, useCallback, useMemo } from "react";

import { graphql } from "@repo/graphql-types/gql";
import type { AtualizarEtapaInput } from "@repo/graphql-types/graphql";
import { UserQueryDocument } from "@repo/graphql-types/graphql";

import doctorWithPatients from "@/assets/doctor-with-patients.png";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  FlowsDataQuery,
  getActiveSteps,
  isStepSkippable,
  useFlowData,
} from "@/hooks/use-flow-data";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { FlowStepContext } from "@/lib/context/flow-step-context";

const UpdateFlowStepMutation = graphql(/* GraphQL */ `
  mutation UpdateFlowStepLayoutMutation($input: AtualizarEtapaInput!) {
    atualizarEtapa(input: $input) {
      sucesso
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

const FlowStepLayout = (): JSX.Element => {
  const { stepId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const { data, isLoading } = useFlowData();
  const { mutateAsync: updateStep, isPending } =
    useGraphQLMutationWithErrorHandler(UpdateFlowStepMutation);
  const invalidateFlowsQuery = useInvalidateQuery(FlowsDataQuery);
  const invalidateUserQuery = useInvalidateQuery(UserQueryDocument, undefined, user.sub);

  const flow = data?.membro?.fluxosPendentes?.[0];
  const steps = useMemo(() => (flow ? getActiveSteps(flow) : []), [flow]);
  const currentIndex = steps.findIndex((s) => String(s.codEtapa) === stepId);
  const currentStep = steps[currentIndex];
  const totalSteps = steps.length;
  const progress = (currentIndex + 1) / (totalSteps + 1);

  const navigateToNextStep = useCallback((): void => {
    const nextIndex = currentIndex + 1;
    const remainingSteps = steps.slice(nextIndex);
    const allRemainingSkippable = remainingSteps.every(isStepSkippable);

    if (allRemainingSkippable) {
      void navigate({
        to: "/flows/complete/$flowId",
        params: { flowId: String(flow?.codFluxo ?? "") },
        replace: true,
      });
      return;
    }

    const next = steps[nextIndex];
    void navigate({
      to: "/flows/$stepId/$subStepId",
      params: {
        stepId: String(next.codEtapa),
        subStepId: "main",
      },
    });
  }, [currentIndex, steps, flow, navigate]);

  const goBack = useCallback((): void => {
    if (currentIndex <= 0) {
      void navigate({ to: "/flows" });
      return;
    }
    const prev = steps[currentIndex - 1];
    void navigate({
      to: "/flows/$stepId/$subStepId",
      params: { stepId: String(prev.codEtapa), subStepId: "main" },
    });
  }, [currentIndex, steps, navigate]);

  const nextStep = useCallback(
    async (step?: AtualizarEtapaInput): Promise<void> => {
      if (step) {
        await updateStep(
          {
            input: {
              codEtapaUsuario: step.codEtapaUsuario,
              status: step.status,
              resposta: step.resposta,
            },
          },
          {
            onSuccess: () => {
              invalidateFlowsQuery();
              invalidateUserQuery();
              navigateToNextStep();
            },
          },
        );
        return;
      }
      navigateToNextStep();
    },
    [updateStep, invalidateFlowsQuery, invalidateUserQuery, navigateToNextStep],
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- se tirar ele quebra
  if (isLoading || !currentStep) {
    return (
      <RoundedLayout>
        <FlowHeader>
          <FlowHeader.Actions>
            <FlowHeader.HelpButton />
          </FlowHeader.Actions>
          <FlowHeader.ProgressBar progress={0} />
        </FlowHeader>
        <FullPageSpinner />
      </RoundedLayout>
    );
  }

  const imageSrc = currentStep.configuracoes?.imagemUrl ?? doctorWithPatients;
  const contextValue = {
    step: currentStep,
    nextStep,
    goBack,
    isLoading: isPending,
    progress,
    imageSrc,
  };

  return (
    <FlowStepContext value={contextValue}>
      <Outlet />
    </FlowStepContext>
  );
};

export const Route = createFileRoute("/flows/$stepId")({
  component: FlowStepLayout,
  loader: ({ context, params }) => {
    const flow = context.user.fluxosPendentes[0];
    const steps = getActiveSteps(flow);
    const stepExists = steps.some((s) => String(s.codEtapa) === params.stepId);

    if (!stepExists) {
      redirect({ to: "/flows", throw: true });
    }
  },
});
